import { useScrollReveal } from '../../hooks/useScrollReveal';

const FEATURES = [
  {
    title: 'Intelligence Warfare',
    desc: 'Information is your greatest weapon. Scouting is expensive, intel decays, and what you don\'t know can destroy you. The fog of war is real.',
    bg: 'rgba(40,80,60,0.2)',
    color: '#6a9e8e',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <path d="M21 21l-4.35-4.35" />
        <circle cx="11" cy="11" r="3" />
      </svg>
    ),
  },
  {
    title: 'Kingdom Cooperation',
    desc: 'Coordinate attacks, share intelligence, and specialize within your kingdom. Solo play is viable — but organized kingdoms dominate.',
    bg: 'rgba(200,168,78,0.15)',
    color: '#c8a84e',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2l1.09 3.26L16 6l-2.18 2.18L14.36 12 12 10.09 9.64 12l.54-3.82L8 6l2.91-.74z" />
      </svg>
    ),
  },
  {
    title: 'Meaningful Tradeoffs',
    desc: "Every building, spell, and military decision has an opportunity cost. There's no optimal build — only the right build for right now.",
    bg: 'rgba(93,204,93,0.12)',
    color: '#5dcc5d',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 3v18M3 12h18" />
        <path d="M8 8l8 8M16 8l-8 8" />
      </svg>
    ),
  },
  {
    title: 'Daily Cycles',
    desc: 'The world advances each day. Armies march, spells expire, resources accumulate. Timing your actions around the daily cycle is an art form.',
    bg: 'rgba(106,158,142,0.15)',
    color: '#6a9e8e',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12,6 12,12 16,14" />
      </svg>
    ),
  },
  {
    title: 'Fresh Ages',
    desc: "Each age resets the world. Keep your kingdom together or strike out into the unknown. New alliances emerge, new strategies rise. No permanent advantage — just skill and cunning.",
    bg: 'rgba(200,100,50,0.15)',
    color: '#e87040',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
      </svg>
    ),
  },
  {
    title: 'New Player Protection',
    desc: '72 hours of protection when you start. Learn the ropes, build your province, and plan your strategy before the real game begins.',
    bg: 'rgba(160,80,200,0.12)',
    color: '#8a6aaa',
    icon: (
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
];

export function FeaturesSection() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section id="features" className="px-6 md:px-12 py-24 max-w-[1200px] mx-auto" ref={ref}>
      <div
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}
      >
        <div
          className="mb-3 uppercase tracking-[0.15em]"
          style={{ fontFamily: "'Cinzel', serif", fontSize: 11, fontWeight: 600, color: '#a08030' }}
        >
          What Makes Galdorn Different
        </div>
        <h2
          className="mb-4"
          style={{ fontFamily: "'Cinzel', serif", fontSize: 36, fontWeight: 700, color: '#f0e8dc', lineHeight: 1.2 }}
        >
          Strategic Depth, Not Pay-to-Win
        </h2>
        <p className="mb-12 max-w-[640px]" style={{ color: '#e0d5c8', fontSize: 18 }}>
          Galdorn is built around meaningful decisions, not grinding or wallet size.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="rounded-lg p-8 transition-all duration-300 hover:border-game-650"
              style={{
                background: '#1c1916',
                border: '1px solid #24201b',
              }}
            >
              <div
                className="flex items-center justify-center rounded-lg mb-5"
                style={{ width: 44, height: 44, background: feature.bg, color: feature.color }}
              >
                {feature.icon}
              </div>
              <div
                className="mb-2"
                style={{ fontFamily: "'Cinzel', serif", fontSize: 16, fontWeight: 600, color: '#f0e8dc' }}
              >
                {feature.title}
              </div>
              <div style={{ color: '#e0d5c8', fontSize: 15, lineHeight: 1.6 }}>{feature.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
